import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { salvarPreferencias, obterConta, salvarConta } from "../services/api";

/**
 * Consentimento LGPD (opt-in por canal). Vale para candidato e empresa.
 * Toda comunicação passa pelo eaiRH (relay) — estes canais são apenas AVISOS.
 *
 * O WhatsApp exige DUAS coisas: o opt-in e o número. Antes dava para marcar a caixa sem ter número
 * cadastrado — e aí nada chegava, em silêncio, sem ninguém entender por quê. Agora o campo do número
 * aparece junto do consentimento, e sem ele o aviso é explícito.
 */
export default function PreferenciasContato() {
  const { user, setPrefs, atualizar } = useAuth();
  const [email, setEmail] = useState(user?.notificarEmail ?? true);
  const [wpp, setWpp] = useState(user?.notificarWhatsapp ?? false);
  const [numero, setNumero] = useState("");
  const [nome, setNome] = useState("");
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // O número mora na conta, não nas preferências — carregamos para poder salvar os dois de uma vez.
  useEffect(() => {
    obterConta().then((c) => { setNumero(c.whatsapp || ""); setNome(c.nome || ""); }).catch(() => {});
  }, []);

  if (!user) return null;

  const soDigitos = (numero || "").replace(/\D/g, "");
  const numeroValido = soDigitos.length >= 10;

  const salvar = async () => {
    setMsg(null); setErro(null);
    if (wpp && !numeroValido) { setErro("Informe um WhatsApp com DDD para receber avisos por lá."); return; }
    setSalvando(true);
    try {
      await salvarPreferencias(email, wpp);
      setPrefs?.(email, wpp);
      // Salva o número junto: marcar o consentimento e esquecer o telefone era o caminho para o silêncio.
      await salvarConta(nome, numero, undefined);
      atualizar?.({ whatsapp: numero });
      setMsg(wpp && numeroValido
        ? "Pronto! Os avisos passam a chegar também no seu WhatsApp."
        : "Preferências salvas.");
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-bold text-gray-900">Preferências de contato (LGPD)</h2>
      <p className="text-sm text-gray-600 mt-1">
        Você autoriza receber avisos do eaiRH por cada canal. Ao se candidatar, a empresa da vaga recebe seu currículo,
        e-mail e WhatsApp (se informado) para falar com você. Nos relatos, a empresa não vê seu contato.
      </p>
      <div className="mt-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Receber avisos por e-mail
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={wpp} onChange={(e) => setWpp(e.target.checked)} /> Receber avisos por WhatsApp
        </label>

        {wpp && (
          <div className="ml-6 mt-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Seu WhatsApp (com DDD)</label>
            <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="(11) 99999-9999"
              className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
            {!numeroValido && (
              <p className="text-[11px] text-amber-700 mt-1">
                Sem o número, o aviso por WhatsApp não sai — e você não seria avisado disso.
              </p>
            )}
            <p className="text-[11px] text-gray-500 mt-1">
              {user.tipo === 1
                ? "Você recebe aviso de candidatura nova e de candidato compatível com suas vagas."
                : "Você recebe aviso de vaga com match, retorno da empresa e resposta ao seu relato."}
            </p>
          </div>
        )}
      </div>
      {msg && <p role="status" className="text-green-700 text-sm mt-2">{msg}</p>}
      {erro && <p role="alert" className="text-red-600 text-sm mt-2">{erro}</p>}
      <button onClick={salvar} disabled={salvando}
        className="mt-3 rounded-lg bg-primary hover:bg-brand disabled:opacity-60 text-white text-sm font-semibold px-4 py-2">
        {salvando ? "Salvando…" : "Salvar"}
      </button>
    </section>
  );
}
